'use client';

import { createContext, useContext, useEffect, useState } from 'react';

interface AuthContextType {
  user: { email: string } | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signIn: async () => {},
  signOut: () => {},
  isAuthenticated: false,
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Admin credentials — change these to your desired credentials
const ADMIN_EMAIL = 'admin@legalNews.com';
const ADMIN_PASSWORD = 'Admin@2024!';
const SESSION_KEY = 'admin_session';
const SESSION_COOKIE = 'admin_session';

function setCookie(name: string, value: string, expiresAt: number) {
  const expires = new Date(expiresAt).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict`;
}

function deleteCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        const session = JSON.parse(stored);
        if (session?.email && session?.expiresAt && Date.now() < session.expiresAt) {
          setUser({ email: session.email });
        } else {
          localStorage.removeItem(SESSION_KEY);
          deleteCookie(SESSION_COOKIE);
        }
      }
    } catch {
      localStorage.removeItem(SESSION_KEY);
      deleteCookie(SESSION_COOKIE);
    }
    setLoading(false);
  }, []);

  const signIn = async (email: string, password: string) => {
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      const expiresAt = Date.now() + 8 * 60 * 60 * 1000; // 8 hours
      const session = { email, expiresAt };
      const sessionStr = JSON.stringify(session);
      localStorage.setItem(SESSION_KEY, sessionStr);
      // Also set a cookie so middleware can validate the session
      setCookie(SESSION_COOKIE, sessionStr, expiresAt);
      setUser({ email });
    } else {
      throw new Error('Invalid email or password');
    }
  };

  const signOut = () => {
    localStorage.removeItem(SESSION_KEY);
    deleteCookie(SESSION_COOKIE);
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
